import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Fade, Flex, Modal, ModalContent, ModalOverlay, Stack, Text, VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

/**
 * Full-screen loading overlay shown while a song generation is in flight.
 * Same shape as pictools's `smart-loading-state.tsx`: a phase-based
 * title/subtitle that progresses over time, a rotating "tip" line
 * underneath, and a Cancel button. Visual is a music-native equalizer
 * bar animation (5 bars, staggered timings) instead of the pixel-shuffle
 * pictools uses — same energy, native to the audio subject.
 *
 * The overlay is a Modal so it captures focus + traps escape properly.
 * `onCancel` is optional — when provided, a Cancel button shows and
 * clicking it hides the overlay (does NOT actually kill the job on the
 * server; there's no cancel endpoint in Mureka's async pipeline).
 */
export function GenerationOverlay({
  isOpen,
  onCancel,
  instrumental,
}: {
  isOpen: boolean;
  onCancel?: () => void;
  instrumental?: boolean;
}) {
  const { t } = useTranslation();
  const phases = instrumental ? INSTRUMENTAL_PHASES : VOCAL_PHASES;
  const tips = instrumental ? INSTRUMENTAL_TIPS : VOCAL_TIPS;

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * tips.length));
  const [tipVisible, setTipVisible] = useState(true);
  const startedAtRef = useRef<number>(0);

  // Reset timers on each open so the overlay always starts at phase 0
  // rather than picking up wherever the previous open left off.
  useEffect(() => {
    if (!isOpen) return;
    startedAtRef.current = Date.now();
    setPhaseIndex(0);
    setTipIndex(Math.floor(Math.random() * tips.length));
    setTipVisible(true);
  }, [isOpen, tips.length]);

  // Advance the phase index by elapsed time. Song generation is longer
  // than image generation (Mureka takes ~30-60s vs pictools ~5-15s), so
  // the thresholds are stretched accordingly.
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      let p = 0;
      for (let i = PHASE_THRESHOLDS.length - 1; i >= 0; i--) {
        if (elapsed >= PHASE_THRESHOLDS[i]) { p = i; break; }
      }
      setPhaseIndex(Math.min(p, phases.length - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, phases.length]);

  // Rotate tips every 7s with a fade — long enough to actually read.
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIndex((i) => (i + 1) % tips.length);
        setTipVisible(true);
      }, 400);
    }, 7000);
    return () => clearInterval(id);
  }, [isOpen, tips.length]);

  const phase = phases[phaseIndex];

  return (
    <Modal isOpen={isOpen} onClose={onCancel || (() => { /* noop when non-cancelable */ })} size="full" motionPreset="none" closeOnOverlayClick={false} closeOnEsc={!!onCancel}>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent bg="transparent" m={0} borderRadius={0} boxShadow="none">
        <Flex h="100dvh" align="center" justify="center" px={5}>
          <VStack spacing={8} maxW="480px" w="100%" textAlign="center">
            {/* Music-native animated visual: 5 equalizer bars */}
            <Equalizer />

            {/* Phase title + subtitle */}
            <Stack spacing={2}>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight={700}
                color="whiteAlpha.900"
                lineHeight="1.25"
                style={{ textWrap: 'balance' as any }}
              >
                {t(phase.titleKey, phase.titleFallback)}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.700">
                {t(phase.subtitleKey, phase.subtitleFallback)}
              </Text>
            </Stack>

            {/* Progress dots (visual indicator, not tied to real backend progress) */}
            <Flex gap="6px">
              {phases.slice(0, -1).map((_, i) => (
                <Box
                  key={i}
                  w="7px"
                  h="7px"
                  borderRadius="full"
                  bg={i <= Math.min(phaseIndex, phases.length - 2) ? 'pink.400' : 'whiteAlpha.300'}
                  transition="background 0.4s ease"
                />
              ))}
            </Flex>

            {/* Shimmer bar */}
            <Box
              w={{ base: '180px', md: '220px' }}
              h="4px"
              borderRadius="full"
              bg="whiteAlpha.200"
              overflow="hidden"
              position="relative"
            >
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(90deg, transparent 0%, pink.400 40%, purple.400 60%, transparent 100%)"
                backgroundSize="200% 100%"
                sx={{
                  '@keyframes mixgenieShimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                  animation: 'mixgenieShimmer 2.2s ease-in-out infinite',
                }}
              />
            </Box>

            {/* Rotating tip */}
            <Box
              minH="3rem"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={4}
              maxW="100%"
            >
              <Fade in={tipVisible} unmountOnExit={false}>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="whiteAlpha.800"
                  fontStyle="italic"
                  style={{ textWrap: 'balance' as any }}
                  maxW="42ch"
                >
                  {t(tips[tipIndex].key, tips[tipIndex].fallback)}
                </Text>
              </Fade>
            </Box>

            {onCancel && (
              <Button
                variant="outline"
                colorScheme="whiteAlpha"
                color="whiteAlpha.900"
                borderColor="whiteAlpha.400"
                onClick={onCancel}
                borderRadius="full"
                px={6}
              >
                {t('generator.cancel', 'Cancel')}
              </Button>
            )}
          </VStack>
        </Flex>
      </ModalContent>
    </Modal>
  );
}

// -----------------------------------------------------------------------
// Equalizer — 5 pink/purple bars that jump on staggered timings. Simpler
// and more on-brand than SVG audio-waveform paths; renders identically on
// mobile + desktop, no external deps.
// -----------------------------------------------------------------------
function Equalizer() {
  return (
    <Flex align="flex-end" justify="center" gap={2} h="110px">
      {[0, 1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          w={{ base: '14px', md: '18px' }}
          bgGradient="linear(to-t, pink.500, purple.500)"
          borderRadius="4px"
          sx={{
            '@keyframes eqBar0': { '0%,100%': { height: '30%' }, '50%': { height: '100%' } },
            '@keyframes eqBar1': { '0%,100%': { height: '65%' }, '50%': { height: '15%' } },
            '@keyframes eqBar2': { '0%,100%': { height: '20%' }, '50%': { height: '85%' } },
            '@keyframes eqBar3': { '0%,100%': { height: '80%' }, '50%': { height: '40%' } },
            '@keyframes eqBar4': { '0%,100%': { height: '45%' }, '50%': { height: '95%' } },
            animation: `eqBar${i} ${0.9 + i * 0.15}s ease-in-out infinite`,
          }}
        />
      ))}
    </Flex>
  );
}

// -----------------------------------------------------------------------
// Copy — phases progress over ~90 s, tips rotate every 7 s. Kept separate
// for vocal vs instrumental so the messages actually match what the model
// is doing.
// -----------------------------------------------------------------------

const PHASE_THRESHOLDS = [0, 8000, 25000, 50000, 80000];

const VOCAL_PHASES = [
  {
    titleKey: 'genOverlay.v.p0.title',
    titleFallback: 'Tuning the instruments…',
    subtitleKey: 'genOverlay.v.p0.subtitle',
    subtitleFallback: 'Setting up the studio',
  },
  {
    titleKey: 'genOverlay.v.p1.title',
    titleFallback: 'Laying down the beat…',
    subtitleKey: 'genOverlay.v.p1.subtitle',
    subtitleFallback: 'Building the rhythm section',
  },
  {
    titleKey: 'genOverlay.v.p2.title',
    titleFallback: 'Recording the vocals…',
    subtitleKey: 'genOverlay.v.p2.subtitle',
    subtitleFallback: 'Coaching the AI on delivery',
  },
  {
    titleKey: 'genOverlay.v.p3.title',
    titleFallback: 'Mixing and mastering…',
    subtitleKey: 'genOverlay.v.p3.subtitle',
    subtitleFallback: 'Getting the levels just right',
  },
  {
    titleKey: 'genOverlay.v.p4.title',
    titleFallback: 'Almost there…',
    subtitleKey: 'genOverlay.v.p4.subtitle',
    subtitleFallback: 'Sometimes the encore takes a moment',
  },
];

const INSTRUMENTAL_PHASES = [
  {
    titleKey: 'genOverlay.i.p0.title',
    titleFallback: 'Tuning the instruments…',
    subtitleKey: 'genOverlay.i.p0.subtitle',
    subtitleFallback: 'Setting up the studio',
  },
  {
    titleKey: 'genOverlay.i.p1.title',
    titleFallback: 'Sketching the arrangement…',
    subtitleKey: 'genOverlay.i.p1.subtitle',
    subtitleFallback: 'Deciding who plays what',
  },
  {
    titleKey: 'genOverlay.i.p2.title',
    titleFallback: 'Recording the sections…',
    subtitleKey: 'genOverlay.i.p2.subtitle',
    subtitleFallback: 'Layering the parts',
  },
  {
    titleKey: 'genOverlay.i.p3.title',
    titleFallback: 'Mixing and mastering…',
    subtitleKey: 'genOverlay.i.p3.subtitle',
    subtitleFallback: 'Getting the levels just right',
  },
  {
    titleKey: 'genOverlay.i.p4.title',
    titleFallback: 'Almost there…',
    subtitleKey: 'genOverlay.i.p4.subtitle',
    subtitleFallback: 'The instrumental bridge is worth the wait',
  },
];

const VOCAL_TIPS = [
  { key: 'genOverlay.tip.v0', fallback: 'Convincing the AI it can carry a tune…' },
  { key: 'genOverlay.tip.v1', fallback: 'Warming up the vocal cords…' },
  { key: 'genOverlay.tip.v2', fallback: 'Debating whether the chorus needs another lift…' },
  { key: 'genOverlay.tip.v3', fallback: 'Adding just the right amount of reverb…' },
  { key: 'genOverlay.tip.v4', fallback: 'Bribing the drummer with a snack…' },
  { key: 'genOverlay.tip.v5', fallback: 'Whispering the lyrics to the machine…' },
  { key: 'genOverlay.tip.v6', fallback: 'Finding the perfect key change…' },
  { key: 'genOverlay.tip.v7', fallback: 'Auditioning drum patterns…' },
  { key: 'genOverlay.tip.v8', fallback: 'Turning it up to eleven…' },
  { key: 'genOverlay.tip.v9', fallback: 'Waiting for the muse to show up…' },
  { key: 'genOverlay.tip.v10', fallback: 'Sampling ideas from the multiverse…' },
  { key: 'genOverlay.tip.v11', fallback: 'Coaxing the strings into harmony…' },
];

const INSTRUMENTAL_TIPS = [
  { key: 'genOverlay.tip.i0', fallback: 'Tuning the guitars…' },
  { key: 'genOverlay.tip.i1', fallback: 'Bribing the drummer with a snack…' },
  { key: 'genOverlay.tip.i2', fallback: 'Debating between B♭ and A♯…' },
  { key: 'genOverlay.tip.i3', fallback: 'Adding just the right amount of reverb…' },
  { key: 'genOverlay.tip.i4', fallback: 'Finding the perfect key change…' },
  { key: 'genOverlay.tip.i5', fallback: 'Making sure the bass drops on cue…' },
  { key: 'genOverlay.tip.i6', fallback: 'Coaxing the strings into harmony…' },
  { key: 'genOverlay.tip.i7', fallback: 'Feeding the metronome…' },
  { key: 'genOverlay.tip.i8', fallback: 'Turning it up to eleven…' },
  { key: 'genOverlay.tip.i9', fallback: 'Sampling ideas from the multiverse…' },
  { key: 'genOverlay.tip.i10', fallback: 'Handpicking notes like a fussy chef…' },
  { key: 'genOverlay.tip.i11', fallback: 'Auditioning drum patterns…' },
];
